import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, catchError, map, shareReplay } from 'rxjs';
import { ColumnDetails } from '../../models/columnDetails.model';
import { BaseTemplateMapper, EntityFieldSource, InlineFieldDescriptor, FieldSourceKind } from './base-template-mapper';

export interface ResolvedGroup {
    key: string;
    label: string;
    accent: string;
    fields: InlineFieldDescriptor[];
}

export interface ResolvedPalette {
    groups: ResolvedGroup[];
    synthetic: InlineFieldDescriptor[];
}

@Injectable({ providedIn: 'root' })
export class TemplateFieldResolverService {
    private readonly configCache = new Map<object, Observable<Record<string, ColumnDetails>>>();

    resolveFields(mapper: BaseTemplateMapper): Observable<ResolvedPalette> {
        const sources = mapper.getEntityFieldSources();
        const groupStreams = sources.map(src =>
            src.kind === FieldSourceKind.BeGrid
                ? this.loadBeGroup(src)
                : of(this.buildInlineGroup(src))
        );
        return forkJoin(groupStreams).pipe(
            map(groups => ({ groups, synthetic: mapper.getSyntheticFields() }))
        );
    }

    private loadBeGroup(src: Extract<EntityFieldSource, { kind: FieldSourceKind.BeGrid }>): Observable<ResolvedGroup> {
        const cached = this.getOrCreateCache(src.service);
        return cached.pipe(
            map(config => {
                const beFields: InlineFieldDescriptor[] = Object.entries(config).map(([prop, details]) => ({
                    path: `${src.pathPrefix}${prop}`,
                    label: details.displayName || this.humanizeFieldName(prop),
                    isImage: false,
                }));

                const overlayMap = new Map<string, InlineFieldDescriptor>();
                for (const descriptor of (src.overlay ?? [])) {
                    overlayMap.set(descriptor.path, descriptor);
                }

                const existingPaths = new Set(beFields.map(f => f.path));
                const mergedFields: InlineFieldDescriptor[] = beFields.map(f =>
                    overlayMap.has(f.path) ? { ...f, ...overlayMap.get(f.path) } : f
                );

                for (const [path, descriptor] of overlayMap.entries()) {
                    if (!existingPaths.has(path)) {
                        mergedFields.push(descriptor);
                    }
                }

                return { key: src.key, label: src.label, accent: src.accent, fields: mergedFields };
            })
        );
    }

    private buildInlineGroup(src: Extract<EntityFieldSource, { kind: FieldSourceKind.Inline }>): ResolvedGroup {
        return { key: src.key, label: src.label, accent: src.accent, fields: src.fields };
    }

    private getOrCreateCache(service: object): Observable<Record<string, ColumnDetails>> {
        if (!this.configCache.has(service)) {
            const stream = (service as { getFieldConfiguration(): Observable<Record<string, ColumnDetails>> })
                .getFieldConfiguration()
                .pipe(
                    catchError(err => {
                        console.error('TemplateFieldResolverService: getFieldConfiguration failed', err);
                        return of({} as Record<string, ColumnDetails>);
                    }),
                    shareReplay({ bufferSize: 1, refCount: true })
                );
            this.configCache.set(service, stream);
        }
        return this.configCache.get(service)!;
    }

    private humanizeFieldName(camelCase: string): string {
        return camelCase
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, s => s.toUpperCase())
            .trim();
    }
}
